import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  FileText, User, Briefcase, Code, Award, Trophy, Star, 
  ExternalLink, Github, Linkedin, Globe, MapPin, Calendar,
  CheckCircle, Zap, Target, TrendingUp, TrendingDown, Eye, Share2, Download,
  Edit3, Save, X, Loader, ChevronRight, Crown, ArrowLeft,
  BookOpen, Building2, GraduationCap, Mail, Phone, Copy,
  Sparkles, CreditCard, Bell, BarChart3, Lock, Unlock, Users, Folder
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { load } from '@cashfreepayments/cashfree-js';
import { getFrontendUrl } from '../utils/urlConfig';

const API = process.env.REACT_APP_BACKEND_URL;

// Swiss design tier colors (flat, no gradients)
const tierColors = {
  Legendary: { bg: '#D97706', text: '#fff' },
  Elite: { bg: '#7C3AED', text: '#fff' },
  Expert: { bg: '#002FA7', text: '#fff' },
  Advanced: { bg: '#059669', text: '#fff' },
  Intermediate: { bg: '#D97706', text: '#fff' },
  Beginner: { bg: '#52525B', text: '#fff' }
};

const ResumeBuilderPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resumeData, setResumeData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    headline: '',
    summary: ''
  });
  
  // Premium & AI states
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiInput, setAiInput] = useState('');
  const [aiType, setAiType] = useState('summary');
  const [pricing, setPricing] = useState({ premium_price: 99, ai_optimization_price: 49 });
  const [purchasingPremium, setPurchasingPremium] = useState(false);
  
  // Analytics states
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  
  // PDF Download
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  
  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  
  // Templates
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('modern');
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  
  // Company Views
  const [companyViews, setCompanyViews] = useState(null);
  const [showCompanyViews, setShowCompanyViews] = useState(false);
  
  // Resume Score
  const [resumeScore, setResumeScore] = useState(null);
  const [loadingScore, setLoadingScore] = useState(false);
  
  // Benchmarks & Peer Comparison
  const [benchmarks, setBenchmarks] = useState(null);
  const [showBenchmarks, setShowBenchmarks] = useState(false);
  const [loadingBenchmarks, setLoadingBenchmarks] = useState(false);
  
  // Skill Gap Analysis
  const [skillGap, setSkillGap] = useState(null);
  const [showSkillGap, setShowSkillGap] = useState(false);
  const [loadingSkillGap, setLoadingSkillGap] = useState(false);
  
  // Score History
  const [scoreHistory, setScoreHistory] = useState(null);
  const [showScoreHistory, setShowScoreHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Badges
  const [badges, setBadges] = useState(null);
  const [showBadges, setShowBadges] = useState(false);
  const [loadingBadges, setLoadingBadges] = useState(false);
  
  // Skill Resources
  const [skillResources, setSkillResources] = useState(null);
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [loadingResources, setLoadingResources] = useState(false);
  
  const token = localStorage.getItem('codearena_token');
  
  useEffect(() => {
    if (!token) {
      navigate('/codearena/auth');
      return;
    }
    fetchResumeData();
    fetchPricing();
    fetchNotifications();
    fetchTemplates();
    fetchResumeScore();
    
    const orderId = searchParams.get('order_id');
    const aiOrderId = searchParams.get('ai_order_id');
    if (orderId) verifyPremiumPurchase(orderId);
    if (aiOrderId) verifyAICredits(aiOrderId);
  }, [token, navigate, searchParams]);
  
  const fetchResumeData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/codearena/me/resume`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResumeData(res.data);
      setEditForm({
        headline: res.data.resume?.headline || '',
        summary: res.data.resume?.summary || ''
      });
    } catch (err) {
      toast.error('Failed to load resume data');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchPricing = async () => {
    try {
      const res = await axios.get(`${API}/api/public/resume-pricing`);
      setPricing(res.data);
    } catch (err) {
      console.error('Failed to fetch pricing');
    }
  };
  
  const fetchNotifications = async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        axios.get(`${API}/api/codearena/me/notifications`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/codearena/me/notifications/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setNotifications(notifRes.data.notifications || []);
      setUnreadCount(countRes.data.unread_count || 0);
    } catch (err) {
      console.error('Failed to fetch notifications');
    }
  };
  
  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/api/codearena/resume/templates`);
      setTemplates(res.data.templates || []);
    } catch (err) {
      console.error('Failed to fetch templates');
    }
  };
  
  const fetchCompanyViews = async () => {
    try {
      const res = await axios.get(`${API}/api/codearena/me/resume/company-views`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanyViews(res.data);
      setShowCompanyViews(true);
    } catch (err) {
      toast.error('Failed to load company views');
    }
  };
  
  const fetchResumeScore = async () => {
    setLoadingScore(true);
    try {
      const res = await axios.get(`${API}/api/codearena/me/resume/score`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResumeScore(res.data);
    } catch (err) {
      console.error('Failed to fetch resume score');
    } finally {
      setLoadingScore(false);
    }
  };
  
  const fetchBenchmarks = async () => {
    setLoadingBenchmarks(true);
    try {
      const res = await axios.get(`${API}/api/codearena/me/resume/benchmarks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBenchmarks(res.data);
      setShowBenchmarks(true);
    } catch (err) {
      toast.error('Failed to load benchmarks');
    } finally {
      setLoadingBenchmarks(false);
    }
  };
  
  const fetchSkillGap = async () => {
    setLoadingSkillGap(true);
    try {
      const res = await axios.get(`${API}/api/codearena/me/resume/skill-gap`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSkillGap(res.data);
      setShowSkillGap(true);
    } catch (err) {
      toast.error('Failed to load skill analysis');
    } finally {
      setLoadingSkillGap(false);
    }
  };
  
  const fetchScoreHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/api/codearena/me/resume/score/history?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScoreHistory(res.data);
      setShowScoreHistory(true);
    } catch (err) {
      toast.error('Failed to load score history');
    } finally {
      setLoadingHistory(false);
    }
  };
  
  const fetchBadges = async () => {
    setLoadingBadges(true);
    try {
      const res = await axios.get(`${API}/api/codearena/me/badges`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBadges(res.data);
      setShowBadges(true);
    } catch (err) {
      toast.error('Failed to load badges');
    } finally {
      setLoadingBadges(false);
    }
  };
  
  const fetchSkillResources = async (skillName) => {
    setLoadingResources(true);
    setSelectedSkill(skillName);
    try {
      const res = await axios.get(`${API}/api/codearena/skill-resources/${encodeURIComponent(skillName)}`);
      setSkillResources(res.data);
    } catch (err) {
      toast.error('Failed to load resources');
    } finally {
      setLoadingResources(false);
    }
  };
  
  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/api/codearena/me/resume/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(res.data);
      setShowAnalytics(true);
    } catch (err) {
      toast.error('Failed to load analytics');
    }
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/codearena/me/resume`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Resume saved!');
      setEditMode(false);
      fetchResumeData();
    } catch (err) {
      toast.error('Failed to save resume');
    } finally {
      setSaving(false);
    }
  };
  
  const handlePurchasePremium = async () => {
    setPurchasingPremium(true);
    try {
      const res = await axios.post(`${API}/api/codearena/resume/purchase-premium`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.payment_session_id) {
        const cashfree = await load({ mode: "production" });
        await cashfree.checkout({
          paymentSessionId: res.data.payment_session_id,
          redirectTarget: "_self"
        });
      } else {
        toast.error('Payment gateway error. Please try again later.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate purchase');
    } finally {
      setPurchasingPremium(false);
    }
  };
  
  const verifyPremiumPurchase = async (orderId) => {
    try {
      const res = await axios.post(`${API}/api/codearena/resume/verify-premium`, 
        { order_id: orderId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      if (res.data.verified) {
        toast.success('Premium activated!');
        fetchResumeData();
      }
    } catch (err) {
      console.error('Premium verification failed');
    }
    window.history.replaceState({}, '', '/codearena/resume');
  };
  
  const verifyAICredits = async (orderId) => {
    try {
      const res = await axios.post(`${API}/api/codearena/resume/verify-ai-credits`,
        { order_id: orderId },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      if (res.data.verified) {
        toast.success(`${res.data.credits_added} AI credits added!`);
        fetchResumeData();
      }
    } catch (err) {
      console.error('AI credits verification failed');
    }
    window.history.replaceState({}, '', '/codearena/resume');
  };
  
  const handleAIGenerate = async () => {
    if (!aiInput.trim()) {
      toast.error('Please enter some information');
      return;
    }
    setAiGenerating(true);
    try {
      const res = await axios.post(`${API}/api/codearena/resume/ai-generate`, {
        type: aiType,
        input: aiInput
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const generated = res.data.generated_content;
      if (aiType === 'summary') {
        setEditForm(prev => ({ ...prev, summary: generated }));
        setEditMode(true);
      }
      toast.success('AI content generated!');
      setShowAIModal(false);
      setAiInput('');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'AI generation failed';
      if (errorMsg.includes('Budget')) {
        toast.error('AI credits exhausted. Please top up your balance.');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setAiGenerating(false);
    }
  };
  
  const copyProfileLink = () => {
    const link = `${getFrontendUrl()}/profile/${resumeData?.resume?.username}`;
    navigator.clipboard.writeText(link);
    toast.success('Profile link copied!');
  };
  
  const copyResumeLink = () => {
    const link = `${getFrontendUrl()}/resume/${resumeData?.resume?.username}`;
    navigator.clipboard.writeText(link);
    toast.success('Resume link copied!');
  };
  
  const handleDownloadPdf = async () => {
    if (!isPremium) {
      toast.error('PDF download is a premium feature. Please upgrade.');
      setShowPremiumModal(true);
      return;
    }
    setDownloadingPdf(true);
    try {
      const response = await axios.get(`${API}/api/codearena/me/resume/download-pdf?template=${selectedTemplate}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const templateName = templates.find(t => t.id === selectedTemplate)?.name || 'Modern';
      a.download = `Resume_${resumeData?.student?.full_name?.replace(' ', '_') || 'Developer'}_${templateName}_EvalentX.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF downloaded successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FAFAFA' }}>
        <Loader className="animate-spin text-[#002FA7]" size={28} />
      </div>
    );
  }
  
  if (!resumeData) return null;
  
  const { resume, student, verified_data } = resumeData;
  const talentScore = verified_data?.talent_score;
  const ranking = verified_data?.ranking;
  const isPremium = resume?.is_premium;
  const aiCredits = resume?.ai_credits || 0;
  const tierStyle = tierColors[talentScore?.tier?.name] || tierColors.Beginner;
  
  return (
    <div className="min-h-screen" style={{ background: '#FAFAFA', fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b" style={{ borderColor: '#E4E4E7' }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-4">
              <Link to="/codearena/dashboard" className="flex items-center gap-2 text-zinc-500 hover:text-zinc-900 transition-colors text-sm font-medium">
                <ArrowLeft size={16} />
                <span className="hidden sm:inline">Dashboard</span>
              </Link>
              <div className="w-px h-5 bg-zinc-200" />
              <div className="flex items-center gap-2">
                <h1 className="text-sm font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}>
                  Verified Developer Resume
                </h1>
                {isPremium && (
                  <span className="text-[9px] font-bold tracking-[0.1em] uppercase px-1.5 py-0.5"
                    style={{ background: '#FFD600', color: '#0A0A0A', borderRadius: '1px' }}>
                    PREMIUM
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Notifications */}
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 text-zinc-500 hover:text-zinc-900 transition-colors"
                  style={{ borderRadius: '2px' }}
                  data-testid="notifications-btn"
                >
                  <Bell size={18} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center"
                      style={{ background: '#DC2626', borderRadius: '1px' }}>
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                
                {showNotifications && (
                  <div className="absolute right-0 top-12 w-80 bg-white border z-50 max-h-96 overflow-hidden"
                    style={{ borderColor: '#E4E4E7', borderRadius: '2px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }}>
                    <div className="p-3 border-b" style={{ borderColor: '#E4E4E7' }}>
                      <h3 className="text-sm font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}>Notifications</h3>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <p className="p-4 text-center text-sm" style={{ color: '#A1A1AA' }}>No notifications yet</p>
                      ) : (
                        notifications.slice(0, 5).map((notif, idx) => (
                          <div key={idx} className="p-3 border-b hover:bg-zinc-50 transition-colors" style={{ borderColor: '#F4F4F5' }}>
                            <p className="text-sm font-medium" style={{ color: '#0A0A0A' }}>{notif.title}</p>
                            <p className="text-xs" style={{ color: '#A1A1AA' }}>{notif.message}</p>
                            <p className="text-xs mt-1" style={{ color: '#A1A1AA', fontFamily: "'IBM Plex Mono', monospace" }}>
                              {new Date(notif.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Analytics */}
              <button onClick={fetchAnalytics}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border transition-colors hover:bg-zinc-50"
                style={{ borderColor: '#E4E4E7', color: '#52525B', borderRadius: '2px' }}
                data-testid="analytics-btn">
                <BarChart3 size={14} /> <span className="hidden sm:inline">Analytics</span>
              </button>
              
              {/* Company Views (Premium) */}
              {isPremium && (
                <button onClick={fetchCompanyViews}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border transition-colors hover:bg-zinc-50"
                  style={{ borderColor: '#002FA720', color: '#002FA7', borderRadius: '2px' }}
                  data-testid="company-views-btn">
                  <Building2 size={14} /> <span className="hidden sm:inline">Who Viewed</span>
                </button>
              )}
              
              {/* Templates (Premium) */}
              {isPremium && (
                <button onClick={() => setShowTemplateModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border transition-colors hover:bg-zinc-50"
                  style={{ borderColor: '#E4E4E7', color: '#52525B', borderRadius: '2px' }}
                  data-testid="templates-btn">
                  <FileText size={14} /> <span className="hidden sm:inline">Templates</span>
                </button>
              )}
              
              {/* AI Generate */}
              <button onClick={() => setShowAIModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold tracking-wide text-white transition-colors"
                style={{ background: '#002FA7', borderRadius: '2px' }}
                onMouseEnter={e => e.target.style.background = '#00227A'}
                onMouseLeave={e => e.target.style.background = '#002FA7'}
                data-testid="ai-generate-btn">
                <Sparkles size={14} /> <span className="hidden sm:inline">AI Generate</span>
              </button>
              
              {/* Upgrade / Download */}
              {!isPremium ? (
                <button onClick={() => setShowPremiumModal(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold tracking-wide text-white transition-colors"
                  style={{ background: '#D97706', borderRadius: '2px' }}
                  data-testid="upgrade-premium-btn">
                  <Crown size={14} /> Upgrade
                </button>
              ) : (
                <button onClick={handleDownloadPdf} disabled={downloadingPdf}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold tracking-wide text-white disabled:opacity-50 transition-colors"
                  style={{ background: '#059669', borderRadius: '2px' }}
                  data-testid="download-pdf-btn">
                  {downloadingPdf ? <Loader className="animate-spin" size={14} /> : <Download size={14} />}
                  PDF
                </button>
              )}
              
              <Link to={`/profile/${resume?.username}`} target="_blank"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border transition-colors hover:bg-zinc-50"
                style={{ borderColor: '#E4E4E7', color: '#0A0A0A', borderRadius: '2px' }}
                data-testid="view-profile-btn">
                <ExternalLink size={14} /> <span className="hidden sm:inline">Profile</span>
              </Link>
            </div>
          </div>
        </div>
      </header>
      
      <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Main Resume Section */}
          <div className="lg:col-span-2 space-y-4">
            {/* Profile Header */}
            <div className="bg-white border p-6" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 flex items-center justify-center text-2xl font-bold"
                    style={{ background: tierStyle.bg, color: tierStyle.text, borderRadius: '2px', fontFamily: "'Space Grotesk', sans-serif" }}>
                    {student?.full_name?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold flex items-center gap-2 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}>
                      {student?.full_name}
                      {resume?.is_verified && <CheckCircle className="text-[#059669]" size={16} />}
                      {isPremium && <Crown className="text-[#D97706]" size={16} />}
                    </h2>
                    {editMode ? (
                      <input
                        type="text"
                        value={editForm.headline}
                        onChange={(e) => setEditForm({ ...editForm, headline: e.target.value })}
                        placeholder="e.g., Full Stack Developer | React & Node.js"
                        className="mt-1 w-full max-w-md px-3 py-1.5 text-sm border bg-white focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7]"
                        style={{ borderColor: '#E4E4E7', borderRadius: '2px', color: '#0A0A0A' }}
                        data-testid="headline-input"
                      />
                    ) : (
                      <p className="text-sm mt-0.5" style={{ color: '#52525B' }}>
                        {resume?.headline || 'Add a professional headline'}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs" style={{ color: '#A1A1AA' }}>
                      {student?.college && (
                        <span className="flex items-center gap-1"><GraduationCap size={12} /> {student.college}</span>
                      )}
                      {student?.location && (
                        <span className="flex items-center gap-1"><MapPin size={12} /> {student.location}</span>
                      )}
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => editMode ? handleSave() : setEditMode(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border transition-colors"
                  style={{
                    borderColor: editMode ? '#059669' : '#E4E4E7',
                    color: editMode ? '#059669' : '#52525B',
                    background: editMode ? '#05966908' : 'transparent',
                    borderRadius: '2px'
                  }}
                  data-testid="edit-save-btn"
                >
                  {saving ? <Loader className="animate-spin" size={14} /> : editMode ? <><Save size={14} /> Save</> : <><Edit3 size={14} /> Edit</>}
                </button>
              </div>
              
              {/* Links */}
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t" style={{ borderColor: '#F4F4F5' }}>
                {student?.github_url && (
                  <a href={student.github_url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border transition-colors hover:bg-zinc-50"
                    style={{ borderColor: '#E4E4E7', color: '#0A0A0A', borderRadius: '2px' }}>
                    <Github size={14} /> GitHub
                  </a>
                )}
                {student?.linkedin_url && (
                  <a href={student.linkedin_url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border transition-colors hover:bg-zinc-50"
                    style={{ borderColor: '#002FA720', color: '#002FA7', borderRadius: '2px' }}>
                    <Linkedin size={14} /> LinkedIn
                  </a>
                )}
                {student?.portfolio_url && (
                  <a href={student.portfolio_url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border transition-colors hover:bg-zinc-50"
                    style={{ borderColor: '#E4E4E7', color: '#52525B', borderRadius: '2px' }}>
                    <Globe size={14} /> Portfolio
                  </a>
                )}
              </div>
            </div>
            
            {/* Summary */}
            <div className="bg-white border p-6" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}>
                  About Me
                </h3>
                <button
                  onClick={() => { setAiType('summary'); setShowAIModal(true); }}
                  className="text-[10px] font-bold tracking-wide flex items-center gap-1 transition-colors"
                  style={{ color: '#002FA7' }}
                >
                  <Sparkles size={11} /> AI GENERATE
                </button>
              </div>
              {editMode ? (
                <textarea
                  value={editForm.summary}
                  onChange={(e) => setEditForm({ ...editForm, summary: e.target.value })}
                  placeholder="Write a brief summary about yourself..."
                  className="w-full px-4 py-3 text-sm border bg-white min-h-[120px] focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7] resize-none"
                  style={{ borderColor: '#E4E4E7', borderRadius: '2px', color: '#0A0A0A' }}
                  data-testid="summary-input"
                />
              ) : (
                <p className="text-sm leading-relaxed" style={{ color: '#52525B' }}>
                  {resume?.summary || student?.bio || 'Add a summary to tell recruiters about yourself'}
                </p>
              )}
            </div>
            
            {/* Skills */}
            {student?.skills?.length > 0 && (
              <div className="bg-white border p-6" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                <h3 className="text-sm font-bold tracking-tight mb-4 flex items-center justify-between" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}>
                  <span className="flex items-center gap-2">Skills</span>
                  <span className="text-[10px] font-bold tracking-[0.1em] uppercase px-2 py-0.5 flex items-center gap-1"
                    style={{ background: '#05966910', color: '#059669', borderRadius: '1px' }}>
                    <CheckCircle size={10} /> VERIFIED
                  </span>
                </h3>
                <div className="flex flex-wrap gap-2">
                  {student.skills.map((skill, idx) => (
                    <span key={idx} className="text-[11px] font-medium px-2.5 py-1"
                      style={{ background: '#F4F4F5', color: '#52525B', borderRadius: '1px' }}>
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Projects */}
            {verified_data?.projects?.length > 0 && (
              <div className="bg-white border p-6" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                <h3 className="text-sm font-bold tracking-tight mb-4 flex items-center justify-between" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}>
                  <span className="flex items-center gap-2">Projects</span>
                  <span className="text-[10px] font-bold tracking-[0.1em] uppercase px-2 py-0.5 flex items-center gap-1"
                    style={{ background: '#05966910', color: '#059669', borderRadius: '1px' }}>
                    <CheckCircle size={10} /> VERIFIED
                  </span>
                </h3>
                <div className="space-y-3">
                  {verified_data.projects.slice(0, 5).map((project, idx) => (
                    <div key={idx} className="p-4 border" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="text-sm font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}>{project.title}</h4>
                          <p className="text-xs mt-1" style={{ color: '#52525B' }}>{project.description}</p>
                          {project.tech_stack && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {project.tech_stack.slice(0, 5).map((tech, i) => (
                                <span key={i} className="text-[10px] font-medium px-2 py-0.5"
                                  style={{ background: '#002FA708', color: '#002FA7', borderRadius: '1px' }}>
                                  {tech}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        {project.github_url && (
                          <a href={project.github_url} target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-zinc-900 transition-colors">
                            <Github size={16} />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Hiring Challenges */}
            {verified_data?.hiring_challenges?.length > 0 && (
              <div className="bg-white border p-6" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                <h3 className="text-sm font-bold tracking-tight mb-4 flex items-center justify-between" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}>
                  <span className="flex items-center gap-2">Hiring Challenges</span>
                  <span className="text-[10px] font-bold tracking-[0.1em] uppercase px-2 py-0.5 flex items-center gap-1"
                    style={{ background: '#05966910', color: '#059669', borderRadius: '1px' }}>
                    <CheckCircle size={10} /> VERIFIED
                  </span>
                </h3>
                <div className="space-y-2">
                  {verified_data.hiring_challenges.map((challenge, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 border" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                      <div>
                        <h4 className="text-sm font-medium" style={{ color: '#0A0A0A' }}>{challenge.title}</h4>
                        <p className="text-xs" style={{ color: '#A1A1AA' }}>{challenge.company}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#002FA7' }}>{challenge.score} pts</span>
                        {challenge.is_shortlisted && (
                          <span className="block text-[10px] font-bold" style={{ color: '#059669' }}>Shortlisted</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Sidebar */}
          <div className="space-y-4">
            {/* Talent Score Card */}
            <div className="bg-white border p-5 overflow-hidden" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
              <div className="h-2 -mx-5 -mt-5 mb-4" style={{ background: tierStyle.bg }} />
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-bold tracking-[0.1em] uppercase" style={{ color: '#A1A1AA' }}>Talent Score</h3>
                <span className="text-[10px] font-bold tracking-[0.1em] uppercase px-2 py-0.5 flex items-center gap-1"
                  style={{ background: '#05966910', color: '#059669', borderRadius: '1px' }}>
                  <CheckCircle size={10} /> VERIFIED
                </span>
              </div>
              <div className="text-center py-3">
                <p className="text-4xl font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#0A0A0A' }}>{talentScore?.score || 0}</p>
                <p className="text-xs mt-0.5" style={{ color: '#A1A1AA' }}>out of 1000</p>
                <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold tracking-[0.06em] uppercase"
                  style={{ background: `${tierStyle.bg}12`, color: tierStyle.bg, borderRadius: '1px' }}>
                  <Crown size={12} />
                  {talentScore?.tier?.name || 'Beginner'}
                </div>
              </div>
            </div>
            
            {/* Ranking */}
            <div className="bg-white border p-5" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
              <h3 className="text-xs font-bold tracking-[0.1em] uppercase mb-3" style={{ color: '#A1A1AA' }}>National Ranking</h3>
              <div className="text-center">
                <p className="text-3xl font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#D97706' }}>#{ranking?.rank || '-'}</p>
                <p className="text-xs" style={{ color: '#A1A1AA' }}>of {ranking?.total_developers || 0} developers</p>
              </div>
            </div>
            
            {/* Resume Score Card */}
            {resumeScore && (
              <div className="bg-white border p-5" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                <h3 className="text-xs font-bold tracking-[0.1em] uppercase mb-4 flex items-center gap-2" style={{ color: '#A1A1AA' }}>
                  <Target size={14} className="text-[#002FA7]" />
                  Resume Score
                </h3>
                
                {/* Circular Progress */}
                <div className="relative w-28 h-28 mx-auto mb-4">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle cx="56" cy="56" r="48" stroke="#F4F4F5" strokeWidth="6" fill="none" />
                    <circle cx="56" cy="56" r="48" stroke={resumeScore.tier.color} strokeWidth="6" fill="none"
                      strokeDasharray={`${(resumeScore.percentage / 100) * 302} 302`} strokeLinecap="butt" className="transition-all duration-1000" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#0A0A0A' }}>{resumeScore.score}</span>
                    <span className="text-[10px]" style={{ color: '#A1A1AA' }}>/ {resumeScore.max_score}</span>
                  </div>
                </div>
                
                <div className="text-center mb-4">
                  <span className="text-[10px] font-bold tracking-[0.1em] uppercase px-2.5 py-1"
                    style={{ background: `${resumeScore.tier.color}12`, color: resumeScore.tier.color, borderRadius: '1px' }}>
                    {resumeScore.tier.name}
                  </span>
                </div>
                
                {/* Score Breakdown */}
                <div className="space-y-2.5 mb-4">
                  {Object.entries(resumeScore.breakdown).map(([key, section]) => (
                    <div key={key} className="flex items-center justify-between text-xs">
                      <span className="capitalize" style={{ color: '#52525B' }}>{key.replace('_', ' ')}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-1" style={{ background: '#F4F4F5', borderRadius: '0' }}>
                          <div className="h-full transition-all" style={{ 
                            width: `${(section.score / section.max) * 100}%`,
                            backgroundColor: section.score === section.max ? '#059669' : section.score >= section.max * 0.5 ? '#D97706' : '#DC2626'
                          }} />
                        </div>
                        <span className="w-10 text-right font-medium" style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#0A0A0A' }}>{section.score}/{section.max}</span>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Top Suggestions */}
                {resumeScore.suggestions.length > 0 && (
                  <div className="pt-4 border-t" style={{ borderColor: '#E4E4E7' }}>
                    <h4 className="text-[10px] font-bold tracking-[0.1em] uppercase mb-3 flex items-center gap-1.5" style={{ color: '#A1A1AA' }}>
                      <Sparkles size={11} className="text-[#D97706]" />
                      BOOST YOUR SCORE
                    </h4>
                    <div className="space-y-2">
                      {resumeScore.suggestions.slice(0, 3).map((suggestion, idx) => (
                        <div key={idx} className="flex items-start gap-2 p-2 text-xs" style={{
                          background: suggestion.impact === 'high' ? '#DC262608' : suggestion.impact === 'medium' ? '#D9770608' : '#05966908',
                          borderRadius: '2px'
                        }}>
                          <span className="mt-0.5 w-1.5 h-1.5 shrink-0" style={{
                            background: suggestion.impact === 'high' ? '#DC2626' : suggestion.impact === 'medium' ? '#D97706' : '#059669',
                            borderRadius: '1px'
                          }} />
                          <span style={{ color: '#52525B' }}>{suggestion.text}</span>
                        </div>
                      ))}
                    </div>
                    {resumeScore.total_suggestions > 3 && (
                      <p className="text-[10px] mt-2 text-center" style={{ color: '#A1A1AA' }}>
                        +{resumeScore.total_suggestions - 3} more suggestions
                      </p>
                    )}
                  </div>
                )}
                
                {/* Action Buttons */}
                <button onClick={fetchBenchmarks} disabled={loadingBenchmarks}
                  className="w-full mt-4 py-2 text-xs font-bold tracking-wide text-white disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
                  style={{ background: '#002FA7', borderRadius: '2px' }}
                  data-testid="compare-peers-btn">
                  {loadingBenchmarks ? <><Loader className="animate-spin" size={14} /> Loading...</> : <><Users size={14} /> Compare with Peers</>}
                </button>
                
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <button onClick={fetchSkillGap} disabled={loadingSkillGap}
                    className="py-2 px-2 text-[10px] font-bold border flex flex-col items-center justify-center gap-1 disabled:opacity-50 transition-colors hover:bg-zinc-50"
                    style={{ borderColor: '#E4E4E7', color: '#059669', borderRadius: '2px' }}
                    data-testid="skill-gap-btn">
                    <Zap size={14} /> Skills
                  </button>
                  <button onClick={fetchScoreHistory} disabled={loadingHistory}
                    className="py-2 px-2 text-[10px] font-bold border flex flex-col items-center justify-center gap-1 disabled:opacity-50 transition-colors hover:bg-zinc-50"
                    style={{ borderColor: '#E4E4E7', color: '#002FA7', borderRadius: '2px' }}
                    data-testid="score-history-btn">
                    <TrendingUp size={14} /> History
                  </button>
                  <button onClick={fetchBadges} disabled={loadingBadges}
                    className="py-2 px-2 text-[10px] font-bold border flex flex-col items-center justify-center gap-1 disabled:opacity-50 transition-colors hover:bg-zinc-50"
                    style={{ borderColor: '#E4E4E7', color: '#D97706', borderRadius: '2px' }}
                    data-testid="badges-btn">
                    <Award size={14} /> Badges
                  </button>
                </div>
              </div>
            )}
            
            {/* Share Links */}
            <div className="bg-white border p-5" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
              <h3 className="text-xs font-bold tracking-[0.1em] uppercase mb-4 flex items-center gap-2" style={{ color: '#A1A1AA' }}>
                <Share2 size={14} /> SHARE PROFILE
              </h3>
              <div className="space-y-2">
                <div className="p-3 border" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                  <p className="text-[10px] font-bold tracking-[0.1em] uppercase mb-1" style={{ color: '#A1A1AA' }}>Resume Link</p>
                  <div className="flex items-center gap-2">
                    <code className="text-xs flex-1 truncate" style={{ color: '#002FA7', fontFamily: "'IBM Plex Mono', monospace" }}>/resume/{resume?.username}</code>
                    <button onClick={copyResumeLink} className="p-1 hover:bg-zinc-50 transition-colors" style={{ borderRadius: '2px' }}>
                      <Copy size={12} className="text-zinc-400" />
                    </button>
                  </div>
                </div>
                <div className="p-3 border" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                  <p className="text-[10px] font-bold tracking-[0.1em] uppercase mb-1" style={{ color: '#A1A1AA' }}>Profile Link</p>
                  <div className="flex items-center gap-2">
                    <code className="text-xs flex-1 truncate" style={{ color: '#002FA7', fontFamily: "'IBM Plex Mono', monospace" }}>/profile/{resume?.username}</code>
                    <button onClick={copyProfileLink} className="p-1 hover:bg-zinc-50 transition-colors" style={{ borderRadius: '2px' }}>
                      <Copy size={12} className="text-zinc-400" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Resume Views */}
            <div className="bg-white border p-5" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
              <h3 className="text-xs font-bold tracking-[0.1em] uppercase mb-3 flex items-center gap-2" style={{ color: '#A1A1AA' }}>
                <Eye size={14} /> VIEWS
              </h3>
              <div className="text-center py-2">
                <p className="text-3xl font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#0A0A0A' }}>{resume?.views_count || 0}</p>
                <p className="text-[10px]" style={{ color: '#A1A1AA' }}>Total Views</p>
              </div>
              <button onClick={fetchAnalytics}
                className="block w-full text-center py-2 text-xs font-bold tracking-wide border transition-colors hover:bg-zinc-50"
                style={{ borderColor: '#E4E4E7', color: '#002FA7', borderRadius: '2px' }}>
                View Analytics
              </button>
            </div>
            
            {/* AI Credits */}
            <div className="bg-white border p-5" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
              <h3 className="text-xs font-bold tracking-[0.1em] uppercase mb-3 flex items-center gap-2" style={{ color: '#A1A1AA' }}>
                <Sparkles size={14} className="text-[#002FA7]" /> AI CREDITS
              </h3>
              <div className="text-center py-2">
                <p className="text-2xl font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#002FA7' }}>{aiCredits}</p>
                <p className="text-[10px]" style={{ color: '#A1A1AA' }}>Available Credits</p>
              </div>
              {!isPremium && (
                <p className="text-[10px] text-center mt-2" style={{ color: '#A1A1AA' }}>
                  Premium users get unlimited AI generations
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* ==================== MODALS ==================== */}
      
      {/* Premium Modal */}
      {showPremiumModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white border max-w-md w-full" style={{ borderColor: '#E4E4E7', borderRadius: '2px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
            <div className="p-6 border-b" style={{ borderColor: '#E4E4E7' }}>
              <div className="flex items-center gap-3 mb-1">
                <Crown size={20} className="text-[#D97706]" />
                <h2 className="text-lg font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}>Upgrade to Premium</h2>
              </div>
              <p className="text-xs" style={{ color: '#A1A1AA' }}>Unlock all features for just <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>INR {pricing.premium_price}</span></p>
            </div>
            <div className="p-6">
              <ul className="space-y-3 mb-6">
                {['Verified Developer Badge', 'ATS Optimized PDF Download', 'Unlimited AI Resume Generations', 'Detailed Resume Analytics', 'Priority in Recruiter Search'].map((feat, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-sm" style={{ color: '#52525B' }}>
                    <CheckCircle className="text-[#059669] shrink-0" size={16} />
                    {feat}
                  </li>
                ))}
              </ul>
              <div className="flex gap-3">
                <button onClick={() => setShowPremiumModal(false)}
                  className="flex-1 py-2.5 text-sm font-medium border transition-colors hover:bg-zinc-50"
                  style={{ borderColor: '#E4E4E7', color: '#52525B', borderRadius: '2px' }}>
                  Maybe Later
                </button>
                <button onClick={handlePurchasePremium} disabled={purchasingPremium}
                  className="flex-1 py-2.5 text-sm font-bold text-white disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                  style={{ background: '#D97706', borderRadius: '2px' }}
                  data-testid="confirm-premium-btn">
                  {purchasingPremium ? <Loader className="animate-spin" size={16} /> : <Crown size={16} />}
                  Get Premium
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* AI Generate Modal */}
      {showAIModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white border max-w-lg w-full" style={{ borderColor: '#E4E4E7', borderRadius: '2px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
            <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: '#E4E4E7' }}>
              <div>
                <h2 className="text-base font-bold flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}>
                  <Sparkles size={18} className="text-[#002FA7]" /> AI Resume Generator
                </h2>
                <p className="text-[10px] mt-0.5" style={{ color: '#A1A1AA' }}>Powered by Athena AI</p>
              </div>
              <button onClick={() => setShowAIModal(false)} className="p-1.5 hover:bg-zinc-50 transition-colors" style={{ borderRadius: '2px' }}>
                <X size={16} className="text-zinc-400" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-[10px] font-bold tracking-[0.1em] uppercase mb-2" style={{ color: '#A1A1AA' }}>GENERATE TYPE</label>
                <select value={aiType} onChange={(e) => setAiType(e.target.value)}
                  className="w-full px-3 py-2 text-sm border bg-white focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7]"
                  style={{ borderColor: '#E4E4E7', borderRadius: '2px', color: '#0A0A0A' }}
                  data-testid="ai-type-select">
                  <option value="summary">Resume Summary</option>
                  <option value="project_description">Project Description</option>
                  <option value="skills_summary">Skills Organization</option>
                  <option value="full_optimization">Full Resume Optimization</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold tracking-[0.1em] uppercase mb-2" style={{ color: '#A1A1AA' }}>
                  {aiType === 'summary' && 'YOUR EXPERIENCE'}
                  {aiType === 'project_description' && 'PROJECT DETAILS'}
                  {aiType === 'skills_summary' && 'YOUR SKILLS'}
                  {aiType === 'full_optimization' && 'CURRENT RESUME'}
                </label>
                <textarea value={aiInput} onChange={(e) => setAiInput(e.target.value)}
                  className="w-full px-3 py-3 text-sm border bg-white min-h-[120px] focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7] resize-none"
                  style={{ borderColor: '#E4E4E7', borderRadius: '2px', color: '#0A0A0A' }}
                  placeholder={
                    aiType === 'summary' ? 'I am a developer with 2 years of experience in React and Node.js...' :
                    aiType === 'project_description' ? 'Built an e-commerce platform using React, Node.js, and MongoDB...' :
                    aiType === 'skills_summary' ? 'Python, JavaScript, React, Node.js, MongoDB, AWS, Docker...' :
                    'Paste your current resume content here...'
                  }
                  data-testid="ai-input" />
              </div>
              {!isPremium && aiCredits <= 0 && (
                <div className="p-3 border text-xs" style={{ borderColor: '#D9770630', background: '#D9770608', color: '#D97706', borderRadius: '2px' }}>
                  You need Premium or AI credits to use this feature.
                </div>
              )}
              <div className="flex gap-3">
                <button onClick={() => setShowAIModal(false)}
                  className="flex-1 py-2.5 text-sm font-medium border transition-colors hover:bg-zinc-50"
                  style={{ borderColor: '#E4E4E7', color: '#52525B', borderRadius: '2px' }}>
                  Cancel
                </button>
                <button onClick={handleAIGenerate} disabled={aiGenerating || (!isPremium && aiCredits <= 0)}
                  className="flex-1 py-2.5 text-sm font-bold text-white disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                  style={{ background: '#002FA7', borderRadius: '2px' }}
                  data-testid="generate-ai-btn">
                  {aiGenerating ? <><Loader className="animate-spin" size={16} /> Generating...</> : <><Sparkles size={16} /> Generate</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Analytics Modal */}
      {showAnalytics && analytics && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white border max-w-lg w-full" style={{ borderColor: '#E4E4E7', borderRadius: '2px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
            <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: '#E4E4E7' }}>
              <h2 className="text-base font-bold flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}>
                <BarChart3 size={18} className="text-[#002FA7]" /> Resume Analytics
              </h2>
              <button onClick={() => setShowAnalytics(false)} className="p-1.5 hover:bg-zinc-50" style={{ borderRadius: '2px' }}>
                <X size={16} className="text-zinc-400" />
              </button>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="p-4 text-center border" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                  <p className="text-2xl font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#0A0A0A' }}>{analytics.total_views}</p>
                  <p className="text-[10px] font-bold tracking-[0.1em] uppercase" style={{ color: '#A1A1AA' }}>Resume Views</p>
                </div>
                <div className="p-4 text-center border" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                  <p className="text-2xl font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#0A0A0A' }}>{analytics.profile_views}</p>
                  <p className="text-[10px] font-bold tracking-[0.1em] uppercase" style={{ color: '#A1A1AA' }}>Profile Views</p>
                </div>
              </div>
              <div className="p-4 border mb-4" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                <p className="text-[10px] font-bold tracking-[0.1em] uppercase mb-1" style={{ color: '#A1A1AA' }}>LAST 30 DAYS</p>
                <p className="text-2xl font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#002FA7' }}>{analytics.views_last_30_days}</p>
              </div>
              {analytics.recent_views?.length > 0 && (
                <div>
                  <h4 className="text-[10px] font-bold tracking-[0.1em] uppercase mb-2" style={{ color: '#A1A1AA' }}>RECENT VIEWS</h4>
                  <div className="space-y-1">
                    {analytics.recent_views.slice(0, 5).map((view, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs py-2 border-b" style={{ borderColor: '#F4F4F5' }}>
                        <span style={{ color: '#52525B' }}>{view.type === 'resume' ? 'Resume' : 'Profile'} viewed</span>
                        <span style={{ color: '#A1A1AA', fontFamily: "'IBM Plex Mono', monospace" }}>{new Date(view.viewed_at).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <button onClick={() => setShowAnalytics(false)}
                className="w-full mt-4 py-2 text-xs font-bold border transition-colors hover:bg-zinc-50"
                style={{ borderColor: '#E4E4E7', color: '#52525B', borderRadius: '2px' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Template Selection Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white border max-w-lg w-full" style={{ borderColor: '#E4E4E7', borderRadius: '2px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
            <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: '#E4E4E7' }}>
              <div>
                <h2 className="text-base font-bold flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}>
                  <FileText size={18} /> Choose Resume Template
                </h2>
                <p className="text-xs mt-0.5" style={{ color: '#A1A1AA' }}>Select a template for your PDF download</p>
              </div>
              <button onClick={() => setShowTemplateModal(false)} className="p-1.5 hover:bg-zinc-50" style={{ borderRadius: '2px' }}>
                <X size={16} className="text-zinc-400" />
              </button>
            </div>
            <div className="p-5">
              <div className="space-y-2">
                {templates.map((template) => (
                  <button key={template.id} onClick={() => setSelectedTemplate(template.id)}
                    className="w-full p-4 border transition-all flex items-center gap-4 text-left"
                    style={{
                      borderColor: selectedTemplate === template.id ? '#002FA7' : '#E4E4E7',
                      background: selectedTemplate === template.id ? '#002FA705' : 'transparent',
                      borderRadius: '2px'
                    }}
                    data-testid={`template-${template.id}`}>
                    <div className="w-10 h-14 border-2 shrink-0" style={{ 
                      borderColor: template.preview_colors?.primary || '#002FA7',
                      backgroundColor: template.preview_colors?.background || '#fff',
                      borderRadius: '1px'
                    }}>
                      <div className="w-full h-1.5 mt-2" style={{ backgroundColor: template.preview_colors?.primary || '#002FA7' }} />
                      <div className="p-1 space-y-0.5">
                        <div className="w-full h-0.5 bg-zinc-200" />
                        <div className="w-3/4 h-0.5 bg-zinc-100" />
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold" style={{ color: '#0A0A0A' }}>{template.name}</h4>
                      <p className="text-xs" style={{ color: '#A1A1AA' }}>{template.description}</p>
                    </div>
                    {selectedTemplate === template.id && <CheckCircle className="ml-auto text-[#002FA7]" size={18} />}
                  </button>
                ))}
              </div>
              <div className="flex gap-3 mt-5">
                <button onClick={() => setShowTemplateModal(false)}
                  className="flex-1 py-2.5 text-sm font-medium border transition-colors hover:bg-zinc-50"
                  style={{ borderColor: '#E4E4E7', color: '#52525B', borderRadius: '2px' }}>
                  Cancel
                </button>
                <button onClick={() => { setShowTemplateModal(false); handleDownloadPdf(); }} disabled={downloadingPdf}
                  className="flex-1 py-2.5 text-sm font-bold text-white disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ background: '#002FA7', borderRadius: '2px' }}
                  data-testid="download-with-template-btn">
                  {downloadingPdf ? <><Loader className="animate-spin" size={16} /> Downloading...</> : <><Download size={16} /> Download PDF</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Company Views Modal */}
      {showCompanyViews && companyViews && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white border max-w-lg w-full" style={{ borderColor: '#E4E4E7', borderRadius: '2px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
            <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: '#E4E4E7' }}>
              <h2 className="text-base font-bold flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}>
                <Building2 size={18} className="text-[#002FA7]" /> Who Viewed Your Resume
              </h2>
              <button onClick={() => setShowCompanyViews(false)} className="p-1.5 hover:bg-zinc-50" style={{ borderRadius: '2px' }}>
                <X size={16} className="text-zinc-400" />
              </button>
            </div>
            <div className="p-5">
              {!companyViews.is_premium ? (
                <div className="text-center py-8">
                  <Lock className="mx-auto mb-4" size={40} style={{ color: '#A1A1AA' }} />
                  <h3 className="text-base font-bold mb-2" style={{ color: '#0A0A0A' }}>Premium Feature</h3>
                  <p className="text-sm mb-4" style={{ color: '#A1A1AA' }}>{companyViews.message}</p>
                  <button onClick={() => { setShowCompanyViews(false); setShowPremiumModal(true); }}
                    className="px-6 py-2 text-sm font-bold text-white" style={{ background: '#D97706', borderRadius: '2px' }}>
                    Upgrade to Premium
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3 mb-5">
                    <div className="p-4 text-center border" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                      <p className="text-2xl font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#002FA7' }}>{companyViews.unique_companies}</p>
                      <p className="text-[10px] font-bold tracking-[0.1em] uppercase" style={{ color: '#A1A1AA' }}>Companies</p>
                    </div>
                    <div className="p-4 text-center border" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                      <p className="text-2xl font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#0A0A0A' }}>{companyViews.total_company_views}</p>
                      <p className="text-[10px] font-bold tracking-[0.1em] uppercase" style={{ color: '#A1A1AA' }}>Total Views</p>
                    </div>
                  </div>
                  {companyViews.company_views?.length > 0 ? (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {companyViews.company_views.map((company, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 border" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 flex items-center justify-center text-sm font-bold"
                              style={{ background: '#002FA7', color: '#fff', borderRadius: '2px' }}>
                              {company.company_name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="text-sm font-medium" style={{ color: '#0A0A0A' }}>{company.company_name}</p>
                              <p className="text-[10px]" style={{ color: '#A1A1AA', fontFamily: "'IBM Plex Mono', monospace" }}>
                                Last: {new Date(company.last_viewed).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#002FA7' }}>{company.view_count}</p>
                            <p className="text-[10px]" style={{ color: '#A1A1AA' }}>views</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Eye className="mx-auto mb-3" size={32} style={{ color: '#A1A1AA' }} />
                      <p className="text-sm" style={{ color: '#A1A1AA' }}>Share your resume to attract recruiters!</p>
                    </div>
                  )}
                </>
              )}
              <button onClick={() => setShowCompanyViews(false)}
                className="w-full mt-4 py-2 text-xs font-bold border transition-colors hover:bg-zinc-50"
                style={{ borderColor: '#E4E4E7', color: '#52525B', borderRadius: '2px' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Benchmarks Modal */}
      {showBenchmarks && benchmarks && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white border max-w-2xl w-full my-8" style={{ borderColor: '#E4E4E7', borderRadius: '2px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
            <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: '#E4E4E7' }}>
              <div>
                <h2 className="text-base font-bold flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}>
                  <Users size={18} className="text-[#002FA7]" /> Industry Benchmarks
                </h2>
                <p className="text-xs mt-0.5" style={{ color: '#A1A1AA' }}>See how you compare with peers</p>
              </div>
              <button onClick={() => setShowBenchmarks(false)} className="p-1.5 hover:bg-zinc-50" style={{ borderRadius: '2px' }}>
                <X size={16} className="text-zinc-400" />
              </button>
            </div>
            <div className="p-5 space-y-5">
              <div className="flex flex-wrap gap-2">
                <span className="text-[10px] font-bold tracking-[0.1em] uppercase px-2.5 py-1 flex items-center gap-1"
                  style={{ background: '#002FA708', color: '#002FA7', borderRadius: '1px' }}>
                  <GraduationCap size={11} /> {benchmarks.college_tier}
                </span>
                <span className="text-[10px] font-bold tracking-[0.1em] uppercase px-2.5 py-1 flex items-center gap-1"
                  style={{ background: '#F4F4F5', color: '#52525B', borderRadius: '1px' }}>
                  <Code size={11} /> {benchmarks.domain}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 border" style={{ borderColor: '#002FA730', background: '#002FA705', borderRadius: '2px' }}>
                  <p className="text-[10px] font-bold tracking-[0.1em] uppercase mb-1" style={{ color: '#A1A1AA' }}>PERCENTILE</p>
                  <p className="text-3xl font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#0A0A0A' }}>Top {benchmarks.peer_group.top_percentile}%</p>
                  <p className="text-xs" style={{ color: '#A1A1AA' }}>of {benchmarks.peer_group.total_peers} developers</p>
                </div>
                <div className="p-4 border" style={{ borderColor: `${benchmarks.standing.color}30`, background: `${benchmarks.standing.color}08`, borderRadius: '2px' }}>
                  <p className="text-[10px] font-bold tracking-[0.1em] uppercase mb-1" style={{ color: '#A1A1AA' }}>STANDING</p>
                  <p className="text-xl font-bold" style={{ color: benchmarks.standing.color }}>{benchmarks.standing.level}</p>
                  <p className="text-xs" style={{ color: '#A1A1AA' }}>{benchmarks.standing.message}</p>
                </div>
              </div>
              {/* Comparison Table */}
              <div className="border overflow-hidden" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                <div className="grid grid-cols-4 gap-2 p-3 text-[10px] font-bold tracking-[0.1em] uppercase" style={{ background: '#F4F4F5', color: '#A1A1AA' }}>
                  <div>Metric</div>
                  <div className="text-center">You</div>
                  <div className="text-center">{benchmarks.college_tier} Avg</div>
                  <div className="text-center">vs Peers</div>
                </div>
                {benchmarks.comparisons.map((comp, idx) => (
                  <div key={idx} className="grid grid-cols-4 gap-2 p-3 border-t items-center" style={{ borderColor: '#F4F4F5' }}>
                    <div className="flex items-center gap-2 text-sm" style={{ color: '#0A0A0A' }}>
                      {comp.icon === 'code' && <Code size={14} className="text-[#002FA7]" />}
                      {comp.icon === 'folder' && <Folder size={14} className="text-[#D97706]" />}
                      {comp.icon === 'check-circle' && <CheckCircle size={14} className="text-[#059669]" />}
                      {comp.icon === 'briefcase' && <Briefcase size={14} className="text-[#52525B]" />}
                      {comp.metric}
                    </div>
                    <div className="text-center text-sm font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#0A0A0A' }}>{comp.yours}</div>
                    <div className="text-center text-sm" style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#A1A1AA' }}>{comp.benchmark}</div>
                    <div className="text-center">
                      {comp.status === 'above' && (
                        <span className="inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-bold"
                          style={{ background: '#05966910', color: '#059669', borderRadius: '1px' }}>
                          <TrendingUp size={10} /> +{comp.diff}
                        </span>
                      )}
                      {comp.status === 'below' && (
                        <span className="inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-bold"
                          style={{ background: '#DC262610', color: '#DC2626', borderRadius: '1px' }}>
                          <TrendingDown size={10} /> {comp.diff}
                        </span>
                      )}
                      {comp.status === 'equal' && (
                        <span className="inline-flex px-2 py-0.5 text-[10px] font-bold"
                          style={{ background: '#F4F4F5', color: '#A1A1AA', borderRadius: '1px' }}>
                          =
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {/* Domain Comparison */}
              <div className="p-4 border" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                <h4 className="text-xs font-bold mb-3 flex items-center gap-2" style={{ color: '#0A0A0A' }}>
                  <BarChart3 size={14} className="text-[#002FA7]" /> {benchmarks.domain} Benchmarks
                </h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  {[
                    { label: 'Avg Skills', bench: benchmarks.domain_benchmark.avg_skills, yours: benchmarks.your_stats.skills_count },
                    { label: 'Avg Projects', bench: benchmarks.domain_benchmark.avg_projects, yours: benchmarks.your_stats.projects_count },
                    { label: 'Avg Score', bench: benchmarks.domain_benchmark.avg_score, yours: benchmarks.your_stats.talent_score }
                  ].map((item, idx) => (
                    <div key={idx}>
                      <p className="text-xl font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#0A0A0A' }}>{item.bench}</p>
                      <p className="text-[10px] font-bold tracking-[0.1em] uppercase" style={{ color: '#A1A1AA' }}>{item.label}</p>
                      <p className="text-xs font-medium" style={{ color: item.yours >= item.bench ? '#059669' : '#DC2626' }}>You: {item.yours}</p>
                    </div>
                  ))}
                </div>
              </div>
              <button onClick={() => setShowBenchmarks(false)}
                className="w-full py-2 text-xs font-bold border transition-colors hover:bg-zinc-50"
                style={{ borderColor: '#E4E4E7', color: '#52525B', borderRadius: '2px' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Skill Gap Modal */}
      {showSkillGap && skillGap && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white border max-w-2xl w-full my-8" style={{ borderColor: '#E4E4E7', borderRadius: '2px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
            <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: '#E4E4E7' }}>
              <div>
                <h2 className="text-base font-bold flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}>
                  <Zap size={18} className="text-[#059669]" /> Skill Gap Analysis
                </h2>
                <p className="text-xs mt-0.5" style={{ color: '#A1A1AA' }}>Recommendations for {skillGap.domain} developers</p>
              </div>
              <button onClick={() => setShowSkillGap(false)} className="p-1.5 hover:bg-zinc-50" style={{ borderRadius: '2px' }}>
                <X size={16} className="text-zinc-400" />
              </button>
            </div>
            <div className="p-5 space-y-5">
              <div className="flex items-center justify-between p-4 border" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                <div>
                  <p className="text-[10px] font-bold tracking-[0.1em] uppercase" style={{ color: '#A1A1AA' }}>Skill Coverage</p>
                  <p className="text-2xl font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#0A0A0A' }}>{skillGap.coverage_percentage}%</p>
                </div>
                <div className="w-32 h-1.5" style={{ background: '#F4F4F5', borderRadius: '0' }}>
                  <div className="h-full transition-all" style={{ width: `${skillGap.coverage_percentage}%`, background: '#059669' }} />
                </div>
              </div>
              {skillGap.learn_next?.length > 0 && (
                <div className="p-4 border" style={{ borderColor: '#05966930', background: '#05966905', borderRadius: '2px' }}>
                  <h4 className="text-xs font-bold mb-3 flex items-center gap-2" style={{ color: '#059669' }}>
                    <Sparkles size={14} /> Learn Next (Priority)
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {skillGap.learn_next.map((skill, idx) => (
                      <span key={idx} className="text-[11px] font-medium px-2.5 py-1"
                        style={{ background: '#05966912', color: '#059669', borderRadius: '1px' }}>
                        {skill.skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {skillGap.missing_essential?.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold mb-3 flex items-center gap-1.5" style={{ color: '#DC2626' }}>
                    <span className="w-2 h-2" style={{ background: '#DC2626', borderRadius: '1px' }} /> Essential Skills Missing
                  </h4>
                  <div className="space-y-2">
                    {skillGap.missing_essential.map((skill, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 border" style={{ borderColor: '#DC262620', background: '#DC262605', borderRadius: '2px' }}>
                        <div>
                          <span className="text-sm font-medium" style={{ color: '#0A0A0A' }}>{skill.skill}</span>
                          <p className="text-[10px]" style={{ color: '#DC2626' }}>{skill.reason}</p>
                        </div>
                        <button onClick={() => fetchSkillResources(skill.skill)}
                          className="px-2.5 py-1 text-[10px] font-bold border flex items-center gap-1 transition-colors hover:bg-zinc-50"
                          style={{ borderColor: '#E4E4E7', color: '#52525B', borderRadius: '2px' }}>
                          <BookOpen size={10} /> Learn
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {skillGap.missing_trending?.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold mb-3 flex items-center gap-1.5" style={{ color: '#D97706' }}>
                    <span className="w-2 h-2" style={{ background: '#D97706', borderRadius: '1px' }} /> Trending Skills
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {skillGap.missing_trending.map((skill, idx) => (
                      <button key={idx} onClick={() => fetchSkillResources(skill.skill)}
                        className="text-[11px] font-medium px-2.5 py-1 transition-colors hover:bg-zinc-100"
                        style={{ background: '#D9770610', color: '#D97706', borderRadius: '1px' }}>
                        {skill.skill}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {skillGap.top_performer_skills?.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold mb-3 flex items-center gap-1.5" style={{ color: '#002FA7' }}>
                    <Crown size={12} /> Skills of Top Performers
                  </h4>
                  <div className="space-y-2">
                    {skillGap.top_performer_skills.map((skill, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 border" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                        <div>
                          <span className="text-sm" style={{ color: '#0A0A0A' }}>{skill.skill}</span>
                          <p className="text-[10px]" style={{ color: '#A1A1AA' }}>{skill.top_performers_with_skill} top devs</p>
                        </div>
                        <button onClick={() => fetchSkillResources(skill.skill)}
                          className="px-2 py-0.5 text-[10px] font-bold border transition-colors hover:bg-zinc-50"
                          style={{ borderColor: '#E4E4E7', color: '#002FA7', borderRadius: '2px' }}>
                          Learn
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Skill Resources */}
              {skillResources && selectedSkill && (
                <div className="p-4 border" style={{ borderColor: '#002FA730', background: '#002FA705', borderRadius: '2px' }}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-bold flex items-center gap-2" style={{ color: '#0A0A0A' }}>
                      <BookOpen size={14} className="text-[#002FA7]" /> Learn {skillResources.skill}
                    </h4>
                    <button onClick={() => { setSkillResources(null); setSelectedSkill(null); }}
                      className="p-1 hover:bg-zinc-50" style={{ borderRadius: '2px' }}>
                      <X size={14} className="text-zinc-400" />
                    </button>
                  </div>
                  {loadingResources ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader className="animate-spin text-[#002FA7]" size={20} />
                    </div>
                  ) : skillResources.found ? (
                    <div className="space-y-2">
                      {skillResources.resources.map((resource, idx) => (
                        <a key={idx} href={resource.url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center justify-between p-2 border transition-colors hover:bg-zinc-50"
                          style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                          <div>
                            <p className="text-sm" style={{ color: '#0A0A0A' }}>{resource.title}</p>
                            <p className="text-[10px]" style={{ color: '#A1A1AA' }}>{resource.platform}</p>
                          </div>
                          <span className="text-[10px] font-bold px-2 py-0.5"
                            style={{
                              background: resource.type === 'official' ? '#002FA710' : resource.type === 'video' ? '#DC262610' : resource.type === 'course' ? '#05966910' : '#F4F4F5',
                              color: resource.type === 'official' ? '#002FA7' : resource.type === 'video' ? '#DC2626' : resource.type === 'course' ? '#059669' : '#52525B',
                              borderRadius: '1px'
                            }}>
                            {resource.type}
                          </span>
                        </a>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm" style={{ color: '#A1A1AA' }}>{skillResources.message}</p>
                  )}
                </div>
              )}
              <button onClick={() => setShowSkillGap(false)}
                className="w-full py-2 text-xs font-bold border transition-colors hover:bg-zinc-50"
                style={{ borderColor: '#E4E4E7', color: '#52525B', borderRadius: '2px' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Score History Modal */}
      {showScoreHistory && scoreHistory && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white border max-w-2xl w-full my-8" style={{ borderColor: '#E4E4E7', borderRadius: '2px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
            <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: '#E4E4E7' }}>
              <div>
                <h2 className="text-base font-bold flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}>
                  <TrendingUp size={18} className="text-[#002FA7]" /> Score History
                </h2>
                <p className="text-xs mt-0.5" style={{ color: '#A1A1AA' }}>Track your resume score over time</p>
              </div>
              <button onClick={() => setShowScoreHistory(false)} className="p-1.5 hover:bg-zinc-50" style={{ borderRadius: '2px' }}>
                <X size={16} className="text-zinc-400" />
              </button>
            </div>
            <div className="p-5 space-y-5">
              <div className="grid grid-cols-3 gap-3">
                <div className="p-4 text-center border" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                  <p className="text-[10px] font-bold tracking-[0.1em] uppercase mb-1" style={{ color: '#A1A1AA' }}>Change</p>
                  <p className="text-xl font-bold" style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    color: scoreHistory.insights.change_direction === 'up' ? '#059669' : scoreHistory.insights.change_direction === 'down' ? '#DC2626' : '#A1A1AA'
                  }}>
                    {scoreHistory.insights.total_change > 0 ? '+' : ''}{scoreHistory.insights.total_change}
                  </p>
                  <p className="text-[10px]" style={{ color: '#A1A1AA' }}>points</p>
                </div>
                <div className="p-4 text-center border" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                  <p className="text-[10px] font-bold tracking-[0.1em] uppercase mb-1" style={{ color: '#A1A1AA' }}>Best</p>
                  <p className="text-xl font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#002FA7' }}>{scoreHistory.insights.best_score}</p>
                  <p className="text-[10px]" style={{ color: '#A1A1AA' }}>achieved</p>
                </div>
                <div className="p-4 text-center border" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                  <p className="text-[10px] font-bold tracking-[0.1em] uppercase mb-1" style={{ color: '#A1A1AA' }}>Average</p>
                  <p className="text-xl font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#0A0A0A' }}>{scoreHistory.insights.average_score}</p>
                  <p className="text-[10px]" style={{ color: '#A1A1AA' }}>score</p>
                </div>
              </div>
              {/* Chart */}
              <div className="p-4 border" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                <h4 className="text-xs font-bold mb-4" style={{ color: '#0A0A0A' }}>Score Trend (Last {scoreHistory.period_days} Days)</h4>
                <div className="h-32 flex items-end gap-1">
                  {scoreHistory.history.map((point, idx) => {
                    const height = (point.score / 100) * 100;
                    return (
                      <div key={idx} className="flex-1 flex flex-col items-center group">
                        <div className="relative w-full">
                          <div className="w-full transition-all" style={{ height: `${height}%`, minHeight: '4px', background: '#002FA7' }} />
                          <div className="absolute -top-7 left-1/2 -translate-x-1/2 hidden group-hover:block px-1.5 py-0.5 text-[10px] text-white whitespace-nowrap"
                            style={{ background: '#0A0A0A', borderRadius: '1px' }}>
                            {point.score} - {point.date}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between mt-2 text-[10px]" style={{ color: '#A1A1AA', fontFamily: "'IBM Plex Mono', monospace" }}>
                  <span>{scoreHistory.history[0]?.date}</span>
                  <span>{scoreHistory.history[scoreHistory.history.length - 1]?.date}</span>
                </div>
              </div>
              {/* Recent */}
              <div>
                <h4 className="text-xs font-bold mb-2" style={{ color: '#0A0A0A' }}>Recent Scores</h4>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {[...scoreHistory.history].reverse().slice(0, 7).map((point, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 border" style={{ borderColor: '#F4F4F5', borderRadius: '2px' }}>
                      <span className="text-xs" style={{ color: '#A1A1AA', fontFamily: "'IBM Plex Mono', monospace" }}>{point.date}</span>
                      <span className="text-sm font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#0A0A0A' }}>{point.score}/100</span>
                    </div>
                  ))}
                </div>
              </div>
              <button onClick={() => setShowScoreHistory(false)}
                className="w-full py-2 text-xs font-bold border transition-colors hover:bg-zinc-50"
                style={{ borderColor: '#E4E4E7', color: '#52525B', borderRadius: '2px' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Badges Modal */}
      {showBadges && badges && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white border max-w-2xl w-full my-8" style={{ borderColor: '#E4E4E7', borderRadius: '2px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
            <div className="p-5 border-b flex items-center justify-between" style={{ borderColor: '#E4E4E7' }}>
              <div>
                <h2 className="text-base font-bold flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}>
                  <Award size={18} className="text-[#D97706]" /> Achievement Badges
                </h2>
                <p className="text-xs mt-0.5" style={{ color: '#A1A1AA' }}>{badges.earned_count} of {badges.total_badges} badges earned</p>
              </div>
              <button onClick={() => setShowBadges(false)} className="p-1.5 hover:bg-zinc-50" style={{ borderRadius: '2px' }}>
                <X size={16} className="text-zinc-400" />
              </button>
            </div>
            <div className="p-5 space-y-5">
              {/* Progress */}
              <div className="p-4 border" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs" style={{ color: '#52525B' }}>Badge Collection</span>
                  <span className="text-sm font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#D97706' }}>{badges.earned_count}/{badges.total_badges}</span>
                </div>
                <div className="w-full h-1.5" style={{ background: '#F4F4F5', borderRadius: '0' }}>
                  <div className="h-full transition-all" style={{ width: `${(badges.earned_count / badges.total_badges) * 100}%`, background: '#D97706' }} />
                </div>
              </div>
              {/* Earned */}
              {badges.earned_badges?.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold mb-3 flex items-center gap-1.5" style={{ color: '#059669' }}>
                    <CheckCircle size={12} /> Earned Badges
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {badges.earned_badges.map((badge, idx) => (
                      <div key={idx} className="p-3 border-2 text-center" style={{ borderColor: badge.color, background: `${badge.color}08`, borderRadius: '2px' }}>
                        <div className="text-2xl mb-1">{badge.emoji}</div>
                        <p className="text-xs font-bold" style={{ color: '#0A0A0A' }}>{badge.name}</p>
                        <p className="text-[10px] mt-0.5" style={{ color: '#A1A1AA' }}>{badge.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* In Progress */}
              {badges.progress_badges?.length > 0 && (
                <div>
                  <h4 className="text-xs font-bold mb-3 flex items-center gap-1.5" style={{ color: '#A1A1AA' }}>
                    <Lock size={12} /> In Progress
                  </h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {badges.progress_badges.slice(0, 6).map((badge, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 border" style={{ borderColor: '#E4E4E7', borderRadius: '2px' }}>
                        <div className="text-xl opacity-40">{badge.emoji}</div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium" style={{ color: '#0A0A0A' }}>{badge.name}</span>
                            <span className="text-[10px]" style={{ color: '#A1A1AA', fontFamily: "'IBM Plex Mono', monospace" }}>{badge.current}/{badge.required}</span>
                          </div>
                          <div className="w-full h-1 mt-1" style={{ background: '#F4F4F5', borderRadius: '0' }}>
                            <div className="h-full transition-all" style={{ width: `${badge.progress}%`, backgroundColor: badge.color }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <button onClick={() => setShowBadges(false)}
                className="w-full py-2 text-xs font-bold border transition-colors hover:bg-zinc-50"
                style={{ borderColor: '#E4E4E7', color: '#52525B', borderRadius: '2px' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResumeBuilderPage;
